import { computed } from 'vue'
import { empty, uuid } from '../shared/utils'
import { communication } from '/@plugins/communication/communication'
import vuexStore from '/@vuex/store'
import { types } from '/@vuex/types'

export type TypeInfo = {
  id: number
  dataType: number
  dataSize: number
  numberOfElements: number
  flags: number
  path: string
  permissions: number
  paramType: string
  groupId: string
  unit: string
}

export const useCommunication = () => {
  const isConnected = computed(() => {
    return vuexStore.getters[types.MOTORCORTEX_ATTRIBUTE]('connection.open')
  })

  const getTreeNodeFor = (path: string) => {
    let result = null

    if (!isConnected.value) {
      return result
    }

    try {
      result = communication.getParameterTree().getChild(path)
    } catch (e) {
      result = null
    }

    return result
  }

  const isPathValid = (path: string) => {
    if (empty(path)) {
      return false
    }

    const node = getTreeNodeFor(path)
    return !empty(node) && empty(node.children)
  }

  const setParameter = (path: string, values:any, settings: any = {}) => {
    return communication.setParameter(path, values, settings)
  }

  const closeConnection = () => {
    return communication.sessionManager.close()
  }

  const dataTypes = communication.messageTypes.DataType

  const getDataType = (dataType: number) => {
    return Object.keys(dataTypes).find((key) => dataTypes[key] === dataType)
  }

  const getDataTypeForPath = (path: string) => {
    return getDataType(getTreeNodeFor(path).info.dataType)
  }

  const getTypeInfoForPath = (path: string): TypeInfo | undefined => {
    return getTreeNodeFor(path)?.info
  }

  /**
   * Coerce a value to the appropriate type for a given parameter path.
   * For string parameters, returns the value as-is.
   * For numeric parameters, converts string values to numbers.
   */
  const coerceValueForPath = (path: string, value: any): any => {
    if (empty(value)) {
      return value
    }

    const dataType = getDataTypeForPath(path)

    // For string parameters, keep the value as-is
    if (dataType === 'STRING') {
      return value
    }

    // For numeric parameters, convert string to number
    const numValue = Number(value)
    return isNaN(numValue) ? value : numValue
  }

  /**
   * Subscribe to parameter path(s) and receive updates when values change.
   * @param paths - Single path or array of paths to subscribe to
   * @param callback - Called with a map of path -> value on each update
   * @param frequencyDivider - Update frequency divider (default: 1)
   * @returns Unsubscribe function
   */
  const subscribe = (
    paths: string | string[],
    callback: (values: Record<string, any>) => void,
    frequencyDivider: number = 1
  ) => {
    const pathArray = Array.isArray(paths) ? paths : [paths]
    const validPaths = pathArray.filter((p) => !empty(p))

    if (validPaths.length === 0 || !isConnected.value) {
      return () => {}
    }

    const groupName = `sub_${uuid()}`
    let subscription: any = null

    try {
      subscription = communication.subscriptionManager.subscribe(validPaths, groupName, frequencyDivider)
      subscription.notify((messages: any[]) => {
        if (messages && messages.length > 0) {
          const values: Record<string, any> = {}
          validPaths.forEach((path, index) => {
            if (messages[index]) {
              values[path] = messages[index].value
            }
          })
          callback(values)
        }
      })
    } catch (e) {
      console.warn('Failed to subscribe to paths:', validPaths, e)
      return () => {}
    }

    return () => {
      if (subscription) {
        try {
          communication.subscriptionManager.unsubscribe(groupName)
        } catch (e) {
          // Ignore unsubscribe errors
        }
      }
    }
  }

  return {
    isConnected,
    getTreeNodeFor,
    setParameter,
    isPathValid,
    closeConnection,
    getDataTypeForPath,
    getTypeInfoForPath,
    getDataType,
    coerceValueForPath,
    subscribe,
  }
}
